import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import GameCanvas from './components/GameCanvas';
import StatsPanel from './components/StatsPanel';
import { GameState } from './types/GameTypes';
import webSocketService from './services/WebSocketService';
import soundService from './services/SoundService';
import MuteButton from './components/MuteButton';
import XPProgressBar from './components/XPProgressBar';
import UptimeDisplay from './components/UptimeDisplay';
import XPManager from './components/XPManager';
import IntroductionPanel from './components/IntroductionPanel';
import FeaturePreview from './components/FeaturePreview';

// Simple Help Icon component (can be moved to a separate file if desired)
const HelpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function App() {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 500 });
  const soundMutedRef = useRef<boolean>(false);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const prevPoopCountRef = useRef<number>(0);
  const prevLevelRef = useRef<number>(1);
  const [showInitialIntro, setShowInitialIntro] = useState<boolean>(false);
  const [showHelpPanel, setShowHelpPanel] = useState<boolean>(false); // State for help panel
  const effectRan = useRef(false); // Ref to track StrictMode double execution

  // Check local storage on mount for the intro panel
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('hasSeenIntro');
    if (hasSeenIntro !== 'true') {
      setShowInitialIntro(true);
    }
  }, []);

  // Connect to the backend on component mount
  useEffect(() => {
    // Check if the effect has already run in this StrictMode cycle
    if (effectRan.current === true || process.env.NODE_ENV !== 'development') {
      console.log("DEV_LOG: Running WebSocket connection setup");

      // Setup socket event handlers
      webSocketService.onConnect(() => {
        console.log("WS Event: Connected");
        setConnected(true);
      });

      webSocketService.onDisconnect(() => {
        console.log("WS Event: Disconnected");
        setConnected(false);
      });

      webSocketService.onGameStateUpdate((newState) => {
        setGameState(newState);
      });

      // Connect to the backend
      webSocketService.connect();
    }

    // Cleanup function
    return () => {
      // Set the ref to true on the first run's cleanup (in StrictMode)
      // On the second run, the main effect logic will execute.
      // On actual unmount, this cleanup still runs.
      if (effectRan.current === true || process.env.NODE_ENV !== 'development') {
        console.log("DEV_LOG: Running WebSocket connection cleanup");
        webSocketService.disconnect();
      }
      effectRan.current = true;
    };
  }, []); // Empty dependency array ensures this runs only on mount/unmount

  // Initialize sound service with current mute state
  useEffect(() => {
    soundService.setMute(soundMutedRef.current);
  }, []);

  // Play sound when a new poop is spawned
  useEffect(() => {
    if (gameState && gameState.objects.poops.length > prevPoopCountRef.current) {
      // A new poop was added, play a fart sound
      soundService.playFartSound();
    }

    // Update the previous poop count reference
    if (gameState) {
      prevPoopCountRef.current = gameState.objects.poops.length;
    }
  }, [gameState]);

  // Play sound when dragon levels up
  useEffect(() => {
    if (gameState && gameState.dragon.level > prevLevelRef.current) {
      // Dragon leveled up, play level up sound
      soundService.playLevelUpSound();
    }

    // Update the previous level reference
    if (gameState) {
      prevLevelRef.current = gameState.dragon.level;
    }
  }, [gameState]);

  // Update canvas size on window resize
  useEffect(() => {
    const handleResize = () => {
      if (mainContainerRef.current) {
        // Get the container width
        const containerWidth = mainContainerRef.current.clientWidth;
        // Calculate a reasonable height (proportional or fixed)
        const containerHeight = Math.min(500, Math.max(300, window.innerHeight * 0.6));

        setCanvasSize({
          width: containerWidth,
          height: containerHeight
        });
      }
    };

    window.addEventListener('resize', handleResize);
    // Initial size calculation
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Disable double-tap zoom throughout the app
  useEffect(() => {
    // This prevents double-tap zooming by setting passive listeners
    document.addEventListener('touchstart', (e) => { }, { passive: false });

    // Also add a specific handler for preventing default touch actions on the app container
    const preventZoom = (e: TouchEvent) => {
      // Prevent the default behavior when we have multiple touch points (pinch to zoom)
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Add the handler with the non-passive option to allow preventDefault
    document.addEventListener('touchmove', preventZoom, { passive: false });

    return () => {
      document.removeEventListener('touchmove', preventZoom);
    };
  }, []);

  const handleMuteChange = () => {
    soundMutedRef.current = !soundMutedRef.current;
    soundService.setMute(soundMutedRef.current);
  };

  // Function to dismiss the intro panel
  const handleDismissIntro = () => {
    setShowInitialIntro(false);
    localStorage.setItem('hasSeenIntro', 'true');
  };

  // Function to toggle the help panel
  const handleToggleHelpPanel = () => {
    setShowHelpPanel(prev => !prev);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8 px-4">
      <div className="container mx-auto max-w-4xl relative" ref={mainContainerRef}>
        {/* Conditionally render the Introduction Panel or Help Panel */}
        {showInitialIntro && <IntroductionPanel onDismiss={handleDismissIntro} />}
        {showHelpPanel && <IntroductionPanel onDismiss={handleToggleHelpPanel} isHelpMode={true} />}

        <header className="mb-8">
          <div className="flex flex-col justify-center items-center">
            <div className="flex flex-row items-center">
              <h1 className="text-3xl font-bold text-orange-500 text-center mr-4">Dragotchi</h1>
              <div className="flex items-center space-x-2">
                <MuteButton onMuteChange={handleMuteChange} initialMuted={soundMutedRef.current} />
                {/* Help Button */}
                <button
                  onClick={handleToggleHelpPanel}
                  className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                  aria-label="Help"
                >
                  <HelpIcon />
                </button>
              </div>
            </div>
            <div className="text-2xl font-bold text-orange-400 mt-2">
              Level {gameState?.dragon.level || 0}
            </div>
          </div>
          <div className="flex justify-center">
            <XPProgressBar
              currentXP={gameState?.dragon.xp || 0}
              requiredXP={gameState?.dragon.xpNeeded || 0}
              level={gameState?.dragon.level || 0}
            />
          </div>
        </header>

        <div className="text-center text-gray-400 mb-4">
          {connected ? 'Connected to server' : 'Connecting to server...'}
          {gameState?.startTime && <UptimeDisplay startTime={gameState.startTime} />}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            {/* Game Canvas */}
            <div className="relative">
              <GameCanvas gameState={gameState} />
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <XPManager />
              </div>
            </div>
            {/* Play Hint */}
            <div className="text-center text-gray-400 mt-2 mb-4">
              <p>Click on items to play with the dragon!</p>
            </div>
          </div>

          <div>
            {/* Stats Panel */}
            <StatsPanel 
              dragonState={gameState?.dragon || null} 
              respawnDurationHours={gameState?.config?.respawnDurationHours}
            />
            {/* Feature Preview Panel - Conditionally render if gameState exists */} 
            {gameState && gameState.dragon && (
              <div className="mt-6">
                <FeaturePreview 
                  currentLevel={gameState.dragon.level} 
                  autoFeatures={gameState.dragon.autoFeatures} 
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
